// File: frontend/src/hooks/usePinnedFirst.ts

import { useEffect, useMemo, useState } from "react";
import apiClient from "../services/apiClient";
import { usePins } from "./usePins";
import { PinTargetType } from "../models/PinModel";

// Detail endpoints used to fetch the pinned topic when it is not part of the
// currently loaded left-pane page. Detail DTOs share the list DTO shape.
const DETAIL_URL: Record<PinTargetType, string> = {
  MATCH: "/api/matches",
  PERSON: "/api/people",
  CLUB: "/api/clubs",
  TEAM: "/api/teams",
  CUSTOM_TOPIC: "/api/custom-topics",
};

/**
 * Hoists the admin-pinned topic of a domain to the top of a left-pane list.
 *
 * - When nothing is pinned for the domain, returns the input unchanged.
 * - When the pinned topic is inside the loaded page, it is moved to index 0.
 * - When it is NOT inside the loaded page (other page / other filter), it is
 *   fetched once by ID and prepended, so the pin stays visible at all times.
 *
 * Returns the reordered items plus the pinned topic id so list components can
 * render the pin badge on the matching row.
 */
export function usePinnedFirst<T extends { id: number }>(
  type: PinTargetType,
  items: T[]
): { orderedItems: T[]; pinnedTopicId: number | null } {
  const { getPinnedTopicId } = usePins();
  const pinnedId = getPinnedTopicId(type);

  // Fetched fallback when the pinned topic is outside the loaded page
  const [fetched, setFetched] = useState<T | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (pinnedId == null) {
      setFetched(null);
      return;
    }
    if (items.some((i) => i.id === pinnedId)) {
      // Already in the list — no extra fetch needed
      setFetched(null);
      return;
    }
    if (fetched && fetched.id === pinnedId) return;
    apiClient
      .get<T>(`${DETAIL_URL[type]}/${pinnedId}`)
      .then((topic) => {
        if (!cancelled) setFetched(topic);
      })
      .catch((err) => {
        // Pin display is a progressive enhancement — never break the list
        console.error("Failed to fetch pinned topic:", err);
        if (!cancelled) setFetched(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedId, items, type]);

  const orderedItems = useMemo(() => {
    if (pinnedId == null) return items;
    const inList = items.find((i) => i.id === pinnedId);
    const rest = items.filter((i) => i.id !== pinnedId);
    // Keep the original object reference — list rows may rely on model methods
    const top = inList ?? (fetched && fetched.id === pinnedId ? fetched : null);
    if (!top) return items;
    return [top, ...rest];
  }, [items, pinnedId, fetched]);

  return { orderedItems, pinnedTopicId: pinnedId };
}

export default usePinnedFirst;